import { z } from "zod"

/**
 * Typed environment variables for Vite-based tint-admin application
 * All variables must be prefixed with VITE_ to be exposed to the client
 */

const EnvSchema = z.object({
  // Vite mode
  MODE: z.enum(["development", "production", "test"]),

  // API Endpoints
  VITE_ADMIN_ENDPOINT: z.string(),
  VITE_ADMIN_API_KEY: z.string(),
  VITE_TIENDANUBE_API_URL: z.string().url(),
  VITE_BILLING_TINTVTO_SERVICE_ENDPOINT: z.string().trim().min(1),
  VITE_PLATFORM_BILLING_TINTVTO_SERVICE_ENDPOINT: z.string().trim().min(1),
  VITE_PLATFORM_ADMIN_TINTVTO_SERVICE_ENDPOINT: z.string().trim().min(1),

  // CDN Configuration
  VITE_CDN_ENDPOINT: z.string(),
  VITE_CDN_UPLOAD_ENDPOINT: z.string(),
  VITE_TINT_MERCHANT_CDN: z.string(),

  // Widget & SDK
  VITE_TINT_WIDGET_LINK: z.string(),
  VITE_SDK_TOKEN: z.string(),

  // Clerk Auth
  VITE_CLERK_PUBLISHABLE_KEY: z.string(),
  VITE_CLERK_SIGN_IN_URL: z.string().url(),
  VITE_CLERK_SIGN_UP_URL: z.string().url(),
  VITE_CLERK_SIGN_IN_FORCE_REDIRECT_URL: z.string().optional(),
  VITE_CLERK_SIGN_UP_FORCE_REDIRECT_URL: z.string().optional(),
  VITE_CLERK_SIGN_IN_FALLBACK_REDIRECT_URL: z.string().optional(),
  VITE_CLERK_SIGN_UP_FALLBACK_REDIRECT_URL: z.string().optional(),

  // Sentry
  VITE_SENTRY_ENV: z.string().optional(),

  // PostHog
  VITE_POSTHOG_KEY: z.string().optional(),
  VITE_POSTHOG_HOST: z.string().url().optional(),
  VITE_POSTHOG_ENABLED: z.enum(["true", "false"]).optional(),
  VITE_POSTHOG_SESSION_RECORDING_ENABLED: z.enum(["true", "false"]).optional(),
})

type Env = z.infer<typeof EnvSchema>

/**
 * Validates and returns typed environment variables
 * Throws an error if validation fails
 */
function getTypedEnv(): Env {
  try {
    return EnvSchema.parse(import.meta.env)
  } catch (error) {
    if (error instanceof z.ZodError) {
      const missingVars = error.errors
        .map((err) => `  - ${err.path.join(".")}: ${err.message}`)
        .join("\n")

      throw new Error(
        `❌ Invalid environment variables:\n${missingVars}\n\n` +
          `Please check your .env file and ensure all required variables are set.`
      )
    }

    throw error
  }
}

/**
 * Type-safe environment variables
 * Use this instead of import.meta.env throughout the application
 *
 * @example
 * import { typedEnv } from '@/config/typedEnv'
 *
 * const apiUrl = typedEnv.VITE_TIENDANUBE_API_URL
 */
export const typedEnv = getTypedEnv()

/**
 * Helper to check if running in development mode
 */
export const isDevelopment = typedEnv.MODE === "development"

/**
 * Helper to check if running in production mode
 */
export const isProduction = typedEnv.MODE === "production"
